import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class IosZoomLockService implements OnDestroy {
  private readonly removeListeners: Array<() => void> = [];
  private lastTouchEndAt = 0;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly ngZone: NgZone
  ) {}

  init(): void {
    if (
      this.removeListeners.length > 0 ||
      !isPlatformBrowser(this.platformId) ||
      !this.isAppleTouchDevice()
    ) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const options: AddEventListenerOptions = { passive: false };
      const preventDefault = (event: Event) => event.preventDefault();
      const preventMultiTouchMove = (event: TouchEvent) => {
        if (event.touches.length > 1) {
          event.preventDefault();
        }
      };
      const preventDoubleTapZoom = (event: TouchEvent) => {
        const now = Date.now();

        if (now - this.lastTouchEndAt <= 300) {
          event.preventDefault();
        }

        this.lastTouchEndAt = now;
      };

      this.listen('gesturestart', preventDefault, options);
      this.listen('gesturechange', preventDefault, options);
      this.listen('gestureend', preventDefault, options);
      this.listen('touchmove', preventMultiTouchMove as EventListener, options);
      this.listen('touchend', preventDoubleTapZoom as EventListener, options);
    });
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((removeListener) => removeListener());
    this.removeListeners.length = 0;
  }

  private listen(type: string, listener: EventListener, options: AddEventListenerOptions): void {
    this.document.addEventListener(type, listener, options);
    this.removeListeners.push(() => this.document.removeEventListener(type, listener, options));
  }

  private isAppleTouchDevice(): boolean {
    const windowRef = this.document.defaultView;

    if (!windowRef) {
      return false;
    }

    const { navigator } = windowRef;

    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }
}
